/**
 * ECharts Theme — Enterprise-quality chart styling.
 *
 * Provides a single `buildBaseOption()` that every chart builder spreads
 * as its foundation.  Individual builders only add chart-specific config.
 *
 * Design principles:
 * - Clean axis lines (hidden ticks/lines, subtle split lines)
 * - Dark tooltip with rounded corners
 * - Compact circle-icon legend
 * - Consistent grid padding with containLabel
 * - No per-series colors — palette set at option.color level
 */
import type { EChartsOption } from 'echarts';

// ── Color palettes ─────────────────────────────────────────────────────────

export const LIGHT_PALETTE = [
  '#3498db', '#2ecc71', '#f1c40f', '#9b59b6', '#e67e22',
  '#1abc9c', '#34495e', '#e74c3c', '#95a5a6', '#16a085',
];

export const DARK_PALETTE = [
  '#5dade2', '#58d68d', '#f7dc6f', '#bb8fce', '#f8c471',
  '#85c1e9', '#82e0aa', '#f9e79f', '#d2b4de', '#fadbd8',
];

// ── Theme interface ────────────────────────────────────────────────────────

export interface ChartTheme {
  mode: 'light' | 'dark';
  colors: string[];
  textColor: string;
  backgroundColor: string;
  gridLineColor: string;
  tooltipBg: string;
  tooltipTextColor: string;
}

export const lightChartTheme: ChartTheme = {
  mode: 'light',
  colors: LIGHT_PALETTE,
  textColor: '#6b7280',
  backgroundColor: 'transparent',
  gridLineColor: '#d1d5db',
  tooltipBg: '#111827',
  tooltipTextColor: '#ffffff',
};

export const darkChartTheme: ChartTheme = {
  mode: 'dark',
  colors: DARK_PALETTE,
  textColor: '#9ca3af',
  backgroundColor: 'transparent',
  gridLineColor: '#4a5568',
  tooltipBg: '#111827',
  tooltipTextColor: '#ffffff',
};

export function getChartTheme(isDark: boolean): ChartTheme {
  return isDark ? darkChartTheme : lightChartTheme;
}

// ── Font stack ─────────────────────────────────────────────────────────────

export const FONT_FAMILY = 'Inter, Segoe UI, system-ui, sans-serif';

// ── Shared config builders ─────────────────────────────────────────────────

export function textStyle(theme: ChartTheme) {
  return {
    fontFamily: FONT_FAMILY,
    fontSize: 12,
    color: theme.textColor,
  };
}

export function gridConfig() {
  return {
    left: 64,
    right: 64,
    top: 32,
    bottom: 32,
    containLabel: true,
  };
}

export function tooltipConfig(theme: ChartTheme, trigger: 'axis' | 'item' = 'axis') {
  return {
    trigger,
    backgroundColor: theme.tooltipBg,
    borderWidth: 0,
    padding: 10,
    textStyle: {
      color: theme.tooltipTextColor,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
    },
    extraCssText: 'border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,0.25);',
    ...(trigger === 'axis'
      ? {
          axisPointer: {
            type: 'line' as const,
            lineStyle: {
              color: '#9ca3af',
              width: 1,
              type: 'dashed' as const,
            },
          },
        }
      : {}),
  };
}

export function legendConfig(theme: ChartTheme) {
  return {
    show: true,
    type: 'scroll' as const,
    top: 4,
    itemWidth: 8,
    itemHeight: 8,
    icon: 'circle' as const,
    itemGap: 14,
    padding: [0, 16, 6, 16] as number[],
    textStyle: {
      fontSize: 11,
      color: theme.textColor,
    },
    // ── Fluent-styled scroll navigation arrows ────────────────────────
    pageIconColor: theme.mode === 'dark' ? '#d1d1d1' : '#424242',
    pageIconInactiveColor: theme.mode === 'dark' ? '#484848' : '#c8c8c8',
    pageIconSize: 12,
    pageButtonItemGap: 8,
    pageButtonGap: 6,
    pageTextStyle: {
      color: theme.textColor,
      fontSize: 11,
      fontFamily: FONT_FAMILY,
    },
    // Fluent chevron SVG paths (24×24 viewBox, matching FluentUI icons)
    pageIcons: {
      horizontal: [
        'M15.707 4.293a1 1 0 0 1 0 1.414L9.414 12l6.293 6.293a1 1 0 0 1-1.414 1.414l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.414 0z',
        'M8.293 4.293a1 1 0 0 1 1.414 0l7 7a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1-1.414-1.414L14.586 12 8.293 5.707a1 1 0 0 1 0-1.414z',
      ],
      vertical: [
        'M4.293 15.707a1 1 0 0 1 1.414 0L12 9.414l6.293 6.293a1 1 0 0 1 1.414-1.414l-7-7a1 1 0 0 1-1.414 0l-7 7a1 1 0 0 1 0 1.414z',
        'M4.293 8.293a1 1 0 0 1 1.414 0L12 14.586l6.293-6.293a1 1 0 0 1 1.414 1.414l-7 7a1 1 0 0 1-1.414 0l-7-7a1 1 0 0 1 0-1.414z',
      ],
    },
  };
}

export function xAxisConfig(theme: ChartTheme) {
  return {
    type: 'category' as const,
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
    nameLocation: 'middle' as const,
    nameTextStyle: { color: theme.textColor, fontSize: 12 },
    nameGap: 40,
    axisLabel: {
      color: theme.textColor,
      fontSize: 12,
      hideOverlap: true,
      overflow: 'truncate' as const,
      width: 80,
    },
  };
}

export function yAxisConfig(theme: ChartTheme) {
  return {
    type: 'value' as const,
    axisLine: { show: false },
    axisTick: { show: false },
    // Axis name shown vertically on the left (set `name` in builder)
    nameLocation: 'middle' as const,
    nameRotate: 90,
    nameGap: 60,
    nameTextStyle: { color: theme.textColor, fontSize: 12 },
    axisLabel: { color: theme.textColor, fontSize: 12 },
    splitLine: {
      lineStyle: { color: theme.gridLineColor, width: 1 },
    },
  };
}

export function animationConfig(animated: boolean) {
  if (!animated) {
    return { animation: false as const };
  }
  return {
    animation: true as const,
    animationDuration: 400,
    animationEasing: 'cubicOut' as const,
  };
}

// ── Base option — merged into every chart ──────────────────────────────────

/**
 * Build the base ECharts option that every chart builder should spread.
 *
 * Contains: palette, animation, textStyle, grid, tooltip, legend defaults.
 * Individual builders override or extend as needed.
 */
export function buildBaseOption(
  theme: ChartTheme,
  animated: boolean,
): EChartsOption {
  return {
    color: theme.colors,
    backgroundColor: theme.backgroundColor,
    ...animationConfig(animated),
    textStyle: textStyle(theme),
    grid: gridConfig(),
    tooltip: tooltipConfig(theme, 'axis'),
    legend: legendConfig(theme),
  };
}